import React from 'react';
import { toFlag } from 'pano-utils';
import { useGate } from 'effector-react';
import { type EditConfigSearch, FeatureType } from '@types';
import useTourEditing from '@hooks/useTourEditing';
import Title from '@components/Title';
import { Collapse } from '@components/Collapse';
import FeatureToggle from '@components/FeatureToggle';
import { Validator } from '@components/Validator';
import { $GateSearchBtn } from '@stores/editingStores';
import PanelBase from '../PanelBase';
import type { PanelBaseProps } from '../types';
import MainToggle from '../components/MainToggle';

const hotspotsToggles: { typeId: FeatureType; name: string }[] = [
  { typeId: FeatureType.link, name: '超链接热点' },
  { typeId: FeatureType.mobile, name: '电话热点' },
  { typeId: FeatureType.map, name: '导航热点' },
  { typeId: FeatureType.media, name: '图文热点' },
  { typeId: FeatureType.text, name: '文本热点' },
  { typeId: FeatureType.article, name: '文章热点' },
  { typeId: FeatureType.doc, name: 'PDF热点' },
  { typeId: FeatureType.audio, name: '音频热点' },
  { typeId: FeatureType.video, name: '视频热点' },
  { typeId: FeatureType.miniProgram, name: '小程序热点' },
  { typeId: FeatureType.seqFrame, name: '逐帧环物热点' },
  { typeId: FeatureType.event, name: '事件热点' },
  { typeId: FeatureType.noFunc, name: '无功能热点' },
];

export function SearchSettingBase({
  defaultValue,
  onConfirm,
}: PanelBaseProps<EditConfigSearch>) {
  const { state, setState, isChanged } = useTourEditing(defaultValue, {
    enabled: 0,
    autoOpen: 1,
    searchInCurrentScene: 0,
  });

  useGate($GateSearchBtn, !!state?.enabled);

  const toggle = (typeId: FeatureType) => {
    setState((draft) => {
      if (!draft) return;
      draft.hotspotTypes ??= [];
      if (draft.hotspotTypes.includes(typeId)) {
        draft.hotspotTypes = draft.hotspotTypes.filter((id) => id !== typeId);
      } else {
        draft.hotspotTypes.push(typeId);
      }
    });
  };

  return (
    <PanelBase
      isChanged={isChanged}
      onConfirm={() => onConfirm(state)}
      title={<Title>搜索设置</Title>}
    >
      <MainToggle
        isOn={!!state?.enabled}
        onToggle={(bool) => {
          setState((draft) => {
            draft.enabled = toFlag(bool);
            if (bool) {
              draft.scenes = 1;
              if (!draft.hotspotTypes?.length) {
                draft.hotspotTypes = hotspotsToggles.map((x) => x.typeId);
              }
            }
          });
        }}
      />
      {!!state?.enabled && (
        <Validator
          value={!!state.scenes || state.hotspotTypes?.length}
          validFn={(value) => !!value}
          warningMessage="请至少开启一个搜索范围内容"
        >
          {() => (
            <Collapse title="搜索范围">
              <FeatureToggle
                title="场景"
                isOn={!!state?.scenes}
                onToggle={(bool) => {
                  setState((draft) => {
                    draft.scenes = toFlag(bool);
                  });
                }}
              />
              {hotspotsToggles.map(({ typeId, name }) => (
                <FeatureToggle
                  title={name}
                  key={typeId}
                  isOn={state?.hotspotTypes?.includes(typeId)}
                  onToggle={() => toggle(typeId)}
                />
              ))}
              <div className="mt-20 text-sm text-orange">
                注：开启后即可进行对应内容的搜索
              </div>
            </Collapse>
          )}
        </Validator>
      )}
      {!!state?.enabled && (
        <Collapse title="搜索交互">
          <FeatureToggle
            title="搜索热点后直接打开热点"
            isOn={!!state?.autoOpen}
            onToggle={(bool) => {
              setState((draft) => {
                draft.autoOpen = toFlag(bool);
              });
            }}
          />
          <FeatureToggle
            title="仅搜索当前场景中的热点"
            isOn={!!state?.searchInCurrentScene}
            onToggle={(bool) => {
              setState((draft) => {
                draft.searchInCurrentScene = toFlag(bool);
              });
            }}
          />
        </Collapse>
      )}
    </PanelBase>
  );
}
